import React, { useState } from "react";
import {
  Plus,
  Play,
  Trash2,
  MoreHorizontal,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SectionHeader from "@/components/v2/SectionHeader";
import EmptyState from "@/components/v2/EmptyState";
import TableSkeleton from "@/components/v2/TableSkeleton";
import ConfirmDialog from "@/components/v2/ConfirmDialog";
import StrategiesSheet from "@/components/v2/StrategiesSheet";
import useStrategies from "@/components/v2/hooks/useStrategies";
import useAlgorithms from "@/components/v2/hooks/useAlgorithms";
import useWebhookSources from "@/components/v2/hooks/useWebhookSources";

export default function StrategiesTab() {
  const { strategies, loading, error, refetch, createStrategy, updateStrategy, deleteStrategy, toggleStrategy, runStrategy } = useStrategies();
  const { algorithms } = useAlgorithms();
  const { sources: webhookSources } = useWebhookSources();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const handleCreate = async (data) => {
    return await createStrategy(data);
  };

  const handleUpdate = async (data) => {
    if (!editingStrategy) return false;
    return await updateStrategy(editingStrategy.id, data);
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    await deleteStrategy(deleteConfirm.id);
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleRun = async (id) => {
    setRunningId(id);
    try {
      await runStrategy(id);
    } catch (e) {
      // error handled in hook
    } finally {
      setRunningId(null);
    }
  };

  const getWebhookName = (id) => {
    if (!id) return null;
    const src = webhookSources.find((s) => String(s.id) === String(id));
    return src?.name || "—";
  };

  if (error) {
    return (
      <div className="bg-surface-2 border border-border rounded-sm p-6">
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-loss" />
          <p className="text-sm text-muted-foreground">Failed to load strategies</p>
          <Button size="sm" variant="outline" onClick={refetch} className="rounded-sm border-border">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 border border-border rounded-sm p-4">
      <SectionHeader
        title="Strategies"
        description="Manage your trading strategies"
        actionLabel="Add Strategy"
        actionIcon={Plus}
        onAction={() => { setEditingStrategy(null); setSheetOpen(true); }}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : strategies.length === 0 ? (
        <EmptyState text="No strategies yet. Create your first strategy to get started." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Algo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Schedule</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Webhook</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {strategies.map((strategy) => (
              <TableRow key={strategy.id} className="border-border">
                <TableCell className="font-medium text-sm">{strategy.name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px] font-mono border-border">
                    {strategy.algo_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs text-muted-foreground">
                    {strategy.schedule_type === "cron"
                      ? strategy.schedule_value
                      : `Every ${strategy.schedule_value}m`}
                  </span>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={strategy.enabled}
                    onCheckedChange={() => toggleStrategy(strategy.id, !strategy.enabled)}
                  />
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {getWebhookName(strategy.webhook_source_id) || "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRun(strategy.id)}
                      disabled={runningId === strategy.id}
                      className="h-7 w-7 p-0 rounded-sm"
                      title="Run Now"
                    >
                      <Play className={`w-3.5 h-3.5 ${runningId === strategy.id ? "animate-pulse" : ""}`} />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm">
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-surface-2 border-border rounded-sm min-w-[130px]">
                        <DropdownMenuItem
                          className="text-xs"
                          onClick={() => handleEdit(strategy)}
                        >
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-xs"
                          onClick={() => handleRun(strategy.id)}
                          disabled={runningId === strategy.id}
                        >
                          Run Now
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-border" />
                        <DropdownMenuItem
                          className="text-xs text-loss"
                          onClick={() => setDeleteConfirm(strategy)}
                        >
                          <Trash2 className="w-3 h-3 mr-1.5" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <StrategiesSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditingStrategy(null); }}
        onSubmit={editingStrategy ? handleUpdate : handleCreate}
        editingStrategy={editingStrategy}
        algorithms={algorithms}
        webhookSources={webhookSources}
      />

      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}
        onConfirm={handleDelete}
        title="Delete Strategy"
        description={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
